import React from 'react';
import TableBadge from '@spark/ui-components/components/TanStackTable/components/TableBadge';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';
import { formatarValorBRL } from '@spark/utils/formatters';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import Icon from '@spark/ui-components/components/Icon';
import Tooltip from '@spark/ui-components/components/Tooltip';
import CheckBox from '@spark/ui-components/components/form/CheckBox';
import InlineDateTimeCell from './InlineDateTimeCell';
import InlineTextCell from './InlineTextCell';

const renderBoolBadge = (value) => {
  if (value === true) {
    return <TableBadge label="Sim" variant="success" />;
  }

  if (value === false) {
    return <TableBadge label="Nao" variant="danger" />;
  }

  return <TableBadge label="N/A" variant="default" />;
};

const renderTipoNotaBadge = (value) => {
  return value === 2 ? (
    <TableBadge label="Devolucao" variant="danger" />
  ) : (
    <TableBadge label="Normal" variant="default" />
  );
};

const renderMotivoDevolucao = (value) => {
  if (value === 1) return 'Erro Cliente';
  if (value === 2) return 'Erro Operador Logistico';
  if (value === 3) return 'Outros';
  return '-';
};

export const getDepositoNotasColumns = (
  onAtualizar,
  loadingAtualizarId,
  onCadastrarDivergencia,
  onEnviarAnexo,
  onEnviarCanhoto,
  onVisualizarCanhoto,
  onAuditoria,
  onFinalizar,
  loadingAcao,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  allPageSelected,
  selectAllDisabled,
  inlineEditConfig
) => {
  const getCellKey = (rowId, columnId) => `${rowId}:${columnId}`;
  const isEditableCell = (rowId, columnId) => inlineEditConfig?.editingCellKey === getCellKey(rowId, columnId);
  const isSavingCell = (rowId, columnId) => inlineEditConfig?.savingCellKey === getCellKey(rowId, columnId);
  const columns = [
    {
      id: 'chaveNFe',
      header: 'Chave NFE',
      accessorKey: 'chaveNFe',
      size: 180,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={22} />
    },
    {
      id: 'numeroNF',
      header: 'Numero NF',
      accessorKey: 'numeroNF',
      size: 110,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={12} />
    },
    {
      id: 'tipoNota',
      header: 'Tipo Nota',
      accessorKey: 'tipoNota',
      size: 110,
      enableSorting: false,
      cell: ({ getValue }) => renderTipoNotaBadge(getValue())
    },
    {
      id: 'descricaoDevolucao',
      header: 'Desc. Devolucao',
      accessorKey: 'descricaoDevolucao',
      size: 190,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={22} />
    },
    {
      id: 'motivoDevolucao',
      header: 'Motivo Devolucao',
      accessorKey: 'motivoDevolucao',
      size: 160,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={renderMotivoDevolucao(getValue())} maxLength={20} />
    },
    {
      id: 'cnpjCliente',
      header: 'CNPJ Cliente',
      accessorKey: 'cnpjCliente',
      size: 140,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={18} />
    },
    {
      id: 'nomeCliente',
      header: 'Nome Cliente',
      accessorKey: 'nomeCliente',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={20} />
    },
    {
      id: 'armazemEndereco',
      header: 'Armazem',
      accessorKey: 'armazemEndereco',
      size: 180,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={20} />
    },
    {
      id: 'localArmazenamento',
      header: 'Local Armazenamento',
      accessorKey: 'localArmazenamento',
      size: 180,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={22} />
    },
    {
      id: 'enderecoFormatado',
      header: 'Endereco',
      accessorKey: 'enderecoFormatado',
      size: 180,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={22} />
    },
    {
      id: 'tipoOperacao',
      header: 'Tipo Operacao',
      accessorKey: 'tipoOperacao',
      size: 140,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={14} />
    },
    {
      id: 'tabelaPreco',
      header: 'Tabela Preco',
      accessorKey: 'tabelaPreco',
      size: 140,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={16} />
    },
    {
      id: 'obsTabelaPreco',
      header: 'Obs. Tabela Preco',
      accessorKey: 'obsTabelaPreco',
      size: 220,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={28} />
    },
    {
      id: 'pendencias',
      header: 'Pendencias',
      accessorKey: 'pendenciasTexto',
      size: 220,
      enableSorting: false,
      cell: ({ row }) => {
        const pendencias = Array.isArray(row.original.pendencias) ? row.original.pendencias : [];
        const texto = pendencias.join(', ');
        return <TruncatedText value={texto || '-'} maxLength={28} />;
      }
    },
    {
      id: 'nomeDestinatario',
      header: 'Destinatario',
      accessorKey: 'nomeDestinatario',
      size: 160,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={18} />
    },
    {
      id: 'nomeRemetente',
      header: 'Remetente',
      accessorKey: 'nomeRemetente',
      size: 160,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={18} />
    },
    {
      id: 'numeroCarga',
      header: 'Numero Carga',
      accessorKey: 'numeroCarga',
      size: 120,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={14} />
    },
    {
      id: 'numeroPedido',
      header: 'Numero Pedido',
      accessorKey: 'numeroPedido',
      size: 130,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={16} />
    },
    {
      id: 'placaVeiculo',
      header: 'Placa Veiculo',
      accessorKey: 'placaVeiculo',
      size: 200,
      enableSorting: false,
      cell: ({ row, getValue }) => (
        <InlineTextCell
          value={getValue() || ''}
          displayMaxLength={10}
          maxLength={10}
          isEditing={isEditableCell(row.original.id, 'placaVeiculo')}
          isSaving={isSavingCell(row.original.id, 'placaVeiculo')}
          onStartEdit={() => inlineEditConfig?.startEdit?.('placaVeiculo', row.original.id)}
          onCommit={(nextValue) => inlineEditConfig?.commitEdit?.({ columnId: 'placaVeiculo', rowId: row.original.id, value: nextValue })}
          onCancel={() => inlineEditConfig?.cancelEdit?.()}
        />
      )
    },
    {
      id: 'numeroFrota',
      header: 'Numero Frota',
      accessorKey: 'numeroFrota',
      size: 110,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={12} />
    },
    {
      id: 'descricaoModeloVeicular',
      header: 'Modelo Veicular',
      accessorKey: 'descricaoModeloVeicular',
      size: 160,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={20} />
    },
    {
      id: 'infoMotorista',
      header: 'Info Motorista',
      accessorKey: 'infoMotorista',
      size: 180,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={30} />
    },
    {
      id: 'modelosReboque',
      header: 'Modelos Reboque',
      accessorKey: 'modelosReboque',
      size: 220,
      enableSorting: false,
      cell: ({ row, getValue }) => (
        <InlineTextCell
          value={getValue() || ''}
          displayMaxLength={30}
          maxLength={500}
          isEditing={isEditableCell(row.original.id, 'modelosReboque')}
          isSaving={isSavingCell(row.original.id, 'modelosReboque')}
          onStartEdit={() => inlineEditConfig?.startEdit?.('modelosReboque', row.original.id)}
          onCommit={(nextValue) => inlineEditConfig?.commitEdit?.({ columnId: 'modelosReboque', rowId: row.original.id, value: nextValue })}
          onCancel={() => inlineEditConfig?.cancelEdit?.()}
        />
      )
    },
    {
      id: 'frotasReboque',
      header: 'Frotas Reboque',
      accessorKey: 'frotasReboque',
      size: 160,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={30} />
    },
    {
      id: 'placasReboque',
      header: 'Placas Reboque',
      accessorKey: 'placasReboque',
      size: 300,
      enableSorting: false,
      cell: ({ row, getValue }) => (
        <InlineTextCell
          value={getValue() || ''}
          displayMaxLength={30}
          maxLength={500}
          isEditing={isEditableCell(row.original.id, 'placasReboque')}
          isSaving={isSavingCell(row.original.id, 'placasReboque')}
          onStartEdit={() => inlineEditConfig?.startEdit?.('placasReboque', row.original.id)}
          onCommit={(nextValue) => inlineEditConfig?.commitEdit?.({ columnId: 'placasReboque', rowId: row.original.id, value: nextValue })}
          onCancel={() => inlineEditConfig?.cancelEdit?.()}
        />
      )
    },
    {
      id: 'dataAgendamentoPedido',
      header: 'Data Agendamento Pedido',
      accessorKey: 'dataAgendamentoPedido',
      size: 190,
      enableSorting: true,
      cell: ({ getValue }) => <span>{getValue() || '-'}</span>
    },
    {
      id: 'descricaoSituacaoDigitalizacaoCanhoto',
      header: 'Situacao Digitalizacao Canhoto',
      accessorKey: 'descricaoSituacaoDigitalizacaoCanhoto',
      size: 200,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={22} />
    },
    {
      id: 'observacoes',
      header: 'Observacoes',
      accessorKey: 'observacoes',
      size: 220,
      enableSorting: false,
      cell: ({ getValue }) => <TruncatedText value={getValue() || '-'} maxLength={28} />
    },
    {
      id: 'dataChegada',
      header: 'Data Chegada',
      accessorKey: 'dataChegada',
      size: 260,
      editable: true,
      editorType: 'datetime',
      payloadKey: 'DataChegada',
      enableSorting: true,
      cell: ({ row, getValue }) => (
        <InlineDateTimeCell
          value={getValue() || '-'}
          isEditing={isEditableCell(row.original.id, 'dataChegada')}
          isSaving={isSavingCell(row.original.id, 'dataChegada')}
          editingValue={inlineEditConfig?.editingInitialValue || ''}
          onStartEdit={() => inlineEditConfig?.startEdit?.('dataChegada', row.original.id)}
          onCommit={(nextValue) => inlineEditConfig?.commitEdit?.({ columnId: 'dataChegada', rowId: row.original.id, value: nextValue })}
          onCancel={() => inlineEditConfig?.cancelEdit?.()}
        />
      )
    },
    {
      id: 'dataAgendamento',
      header: 'Data Agendamento',
      accessorKey: 'dataAgendamento',
      size: 260,
      editable: true,
      editorType: 'datetime',
      payloadKey: 'DataAgendamento',
      enableSorting: true,
      cell: ({ row, getValue }) => (
        <InlineDateTimeCell
          value={getValue() || '-'}
          isEditing={isEditableCell(row.original.id, 'dataAgendamento')}
          isSaving={isSavingCell(row.original.id, 'dataAgendamento')}
          editingValue={inlineEditConfig?.editingInitialValue || ''}
          onStartEdit={() => inlineEditConfig?.startEdit?.('dataAgendamento', row.original.id)}
          onCommit={(nextValue) => inlineEditConfig?.commitEdit?.({ columnId: 'dataAgendamento', rowId: row.original.id, value: nextValue })}
          onCancel={() => inlineEditConfig?.cancelEdit?.()}
        />
      )
    },
    {
      id: 'dataLeitura',
      header: 'Data Leitura',
      accessorKey: 'dataLeitura',
      size: 170,
      enableSorting: true,
      cell: ({ getValue }) => <span>{getValue() || '-'}</span>
    },
    {
      id: 'dataSaida',
      header: 'Data Saida/Entrega',
      accessorKey: 'dataSaida',
      size: 260,
      editable: true,
      editorType: 'datetime',
      payloadKey: 'DataSaida',
      enableSorting: true,
      cell: ({ row, getValue }) => (
        <InlineDateTimeCell
          value={getValue() || '-'}
          isEditing={isEditableCell(row.original.id, 'dataSaida')}
          isSaving={isSavingCell(row.original.id, 'dataSaida')}
          editingValue={inlineEditConfig?.editingInitialValue || ''}
          onStartEdit={() => inlineEditConfig?.startEdit?.('dataSaida', row.original.id)}
          onCommit={(nextValue) => inlineEditConfig?.commitEdit?.({ columnId: 'dataSaida', rowId: row.original.id, value: nextValue })}
          onCancel={() => inlineEditConfig?.cancelEdit?.()}
        />
      )
    },
    {
      id: 'quantidadeDias',
      header: 'Qtd. Dias',
      accessorKey: 'quantidadeDias',
      size: 100,
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue();
        return <span>{value === null || value === undefined ? '-' : String(value)}</span>;
      }
    },
    {
      id: 'quantidadeTotalPaletes',
      header: 'Qtde Paletes',
      accessorKey: 'quantidadeTotalPaletes',
      size: 120,
      enableSorting: false,
      cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
    },
    {
      id: 'quantidadePaletesCobranca',
      header: 'Qtde Paletes Cobranca',
      accessorKey: 'quantidadePaletesCobranca',
      size: 160,
      enableSorting: false,
      cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
    },
    {
      id: 'quantidadeTotalEtiquetas',
      header: 'Qtde Etiquetas',
      accessorKey: 'quantidadeTotalEtiquetas',
      size: 120,
      enableSorting: false,
      cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
    },
    {
      id: 'quantidadeEtiquetasCobranca',
      header: 'Qtde Etiquetas Cobranca',
      accessorKey: 'quantidadeEtiquetasCobranca',
      size: 170,
      enableSorting: false,
      cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
    },
    {
      id: 'quantidadeTotalCaixas',
      header: 'Qtde Caixas',
      accessorKey: 'quantidadeTotalCaixas',
      size: 110,
      enableSorting: false,
      cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
    },
    {
      id: 'valorUnPallet',
      header: 'Valor Pallet',
      accessorKey: 'valorUnPallet',
      size: 120,
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue();
        return value === null || value === undefined ? '-' : formatarValorBRL(value);
      }
    },
    {
      id: 'valorUnEtiqueta',
      header: 'Valor Etiqueta',
      accessorKey: 'valorUnEtiqueta',
      size: 120,
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue();
        return value === null || value === undefined ? '-' : formatarValorBRL(value);
      }
    },
    {
      id: 'valorUnArmazenagemPallet',
      header: 'Valor Arm. Pallet',
      accessorKey: 'valorUnArmazenagemPallet',
      size: 140,
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue();
        return value === null || value === undefined ? '-' : formatarValorBRL(value);
      }
    },
    {
      id: 'valorTotalPendenteArmazenagem',
      header: 'Valor Total Pendente Armazenagem',
      accessorKey: 'valorTotalPendenteArmazenagem',
      size: 200,
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue();
        return value === null || value === undefined ? '-' : formatarValorBRL(value);
      }
    },
    {
      id: 'valorTotalPendentePaletizacao',
      header: 'Valor Total Pendente Paletizacao',
      accessorKey: 'valorTotalPendentePaletizacao',
      size: 200,
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue();
        return value === null || value === undefined ? '-' : formatarValorBRL(value);
      }
    },
    {
      id: 'valorTotalPendenteEtiquetagem',
      header: 'Valor Total Pendente Etiquetagem',
      accessorKey: 'valorTotalPendenteEtiquetagem',
      size: 200,
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue();
        return value === null || value === undefined ? '-' : formatarValorBRL(value);
      }
    },
    {
      id: 'freetime',
      header: 'Freetime',
      accessorKey: 'freetime',
      size: 90,
      enableSorting: false,
      cell: ({ getValue }) => <span>{getValue() ?? '-'}</span>
    },
    {
      id: 'diasExcedidos',
      header: 'Dias Excedidos',
      accessorKey: 'diasExcedidos',
      size: 120,
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue();
        return <span>{value === null || value === undefined ? '-' : String(value)}</span>;
      }
    },
    {
      id: 'existeDivergencia',
      header: 'Divergencia',
      accessorKey: 'existeDivergencia',
      size: 120,
      enableSorting: false,
      cell: ({ getValue }) => renderBoolBadge(getValue())
    }
  ];

  if (onAtualizar || onCadastrarDivergencia || onEnviarAnexo || onEnviarCanhoto || onVisualizarCanhoto || onAuditoria || onFinalizar) {
    const headerContent = onToggleSelect ? (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        
        {onToggleSelectAll && (
          <Tooltip dir="top" text={allPageSelected ? 'Remover selecao da pagina' : 'Selecionar pagina'}>
            <span className="d-inline-flex align-items-center">
              <CheckBox
                name="select-all-deposito"
                checked={!!allPageSelected}
                disabled={!!selectAllDisabled}
                label=""
                onChange={(event) => {
                  event.stopPropagation();
                  onToggleSelectAll();
                }}
                onClick={(event) => event.stopPropagation()}
              />
            </span>
          </Tooltip>
        )}
        <span>Acoes</span>
      </div>
    ) : (
      'Acoes'
    );

    const actionsColumn = {
      id: 'acoes',
      header: () => headerContent,
      accessorKey: 'id',
      size: 400,
      enableHiding: false,
      enablePinning: true,
      enableSorting: false,
      enableCopy: false,
      cell: ({ row }) => (
        <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
          {onToggleSelect && (
            <Tooltip dir="top" text={selectedIds?.includes(row.original.id) ? 'Remover selecao' : 'Selecionar'}>
              <span className="d-inline-flex align-items-center">
                <CheckBox
                  name={`select-${row.original.id}`}
                  checked={selectedIds?.includes(row.original.id)}
                  label=""
                  onChange={(event) => {
                    event.stopPropagation();
                    onToggleSelect(row.original);
                  }}
                  onClick={(event) => event.stopPropagation()}
                />
              </span>
            </Tooltip>
          )}
          {onAtualizar && (
            <Tooltip dir="top" text="Atualizar">
              <span className="d-inline-block">
                <ButtonWithLoading
                  variant="outline-primary"
                  size="sm"
                  loading={loadingAtualizarId === row.original.id}
                  disabled={loadingAtualizarId === row.original.id}
                  loadingText=""
                  style={{ minWidth: 'auto' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onAtualizar(row.original);
                  }}
                >
                  <Icon name="pencil" size={14} />
                </ButtonWithLoading>
              </span>
            </Tooltip>
          )}
          {onCadastrarDivergencia && (
            <Tooltip dir="top" text="Cadastrar divergencia">
              <span className="d-inline-block">
                <ButtonWithLoading
                  variant="outline-warning"
                  size="sm"
                  loading={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'divergencia'}
                  disabled={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'divergencia'}
                  loadingText=""
                  style={{ minWidth: 'auto' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onCadastrarDivergencia(row.original);
                  }}
                >
                  <Icon name="comment-alert-outline" size={14} />
                </ButtonWithLoading>
              </span>
            </Tooltip>
          )}
          {onEnviarAnexo && (
            <Tooltip dir="top" text="Enviar anexo">
              <span className="d-inline-block">
                <ButtonWithLoading
                  variant="outline-secondary"
                  size="sm"
                  loading={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'anexo'}
                  disabled={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'anexo'}
                  loadingText=""
                  style={{ minWidth: 'auto' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onEnviarAnexo(row.original);
                  }}
                >
                  <Icon name="paperclip" size={14} />
                </ButtonWithLoading>
              </span>
            </Tooltip>
          )}
          {onEnviarCanhoto && (
            <Tooltip dir="top" text="Enviar canhoto">
              <span className="d-inline-block">
                <ButtonWithLoading
                  variant="outline-secondary"
                  size="sm"
                  loading={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'canhoto'}
                  disabled={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'canhoto'}
                  loadingText=""
                  style={{ minWidth: 'auto' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onEnviarCanhoto(row.original);
                  }}
                >
                  <Icon name="file-document-outline" size={14} />
                </ButtonWithLoading>
              </span>
            </Tooltip>
          )}
          {onVisualizarCanhoto && (
            <Tooltip dir="top" text="Visualizar Canhoto">
              <span className="d-inline-block">
                <ButtonWithLoading
                  variant="outline-secondary"
                  size="sm"
                  loading={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'visualizar-canhoto'}
                  disabled={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'visualizar-canhoto'}
                  loadingText=""
                  style={{ minWidth: 'auto' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onVisualizarCanhoto(row.original);
                  }}
                >
                  <Icon name="file-image" size={14} />
                </ButtonWithLoading>
              </span>
            </Tooltip>
          )}
          {onAuditoria && (
            <Tooltip dir="top" text="Visualizar auditoria">
              <span className="d-inline-block">
                <ButtonWithLoading
                  variant="outline-info"
                  size="sm"
                  loading={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'auditoria'}
                  disabled={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'auditoria'}
                  loadingText=""
                  style={{ minWidth: 'auto' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onAuditoria(row.original);
                  }}
                >
                  <Icon name="eye" size={14} />
                </ButtonWithLoading>
              </span>
            </Tooltip>
          )}
          {onFinalizar && (
            <Tooltip dir="top" text="Finalizar nota">
              <span className="d-inline-block">
                <ButtonWithLoading
                  variant="outline-success"
                  size="sm"
                  loading={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'finalizar'}
                  disabled={loadingAcao?.id === row.original.id && loadingAcao?.acao === 'finalizar'}
                  loadingText=""
                  style={{ minWidth: 'auto' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onFinalizar(row.original);
                  }}
                >
                  <Icon name="check" size={14} />
                </ButtonWithLoading>
              </span>
            </Tooltip>
          )}
        </div>
      )
    };

    columns.unshift(actionsColumn);
  }

  return columns;
};
